import { useState, useEffect, useCallback } from 'react';
import { CART_ADD_URL, CART_VIEW_URL, CART_UPDATE_URL, CART_REMOVE_ITEM_URL } from '../../config/api';
import { authenticatedFetch } from './apiHelpers';
import { trackFood } from './analytics';

export interface CartItemState {
  quantity: number;
  cartId: number;
  loading: boolean;
}

export interface UseCartOptions<T> {
  cartRefreshToken?: number;
  onCartChange?: () => void;
  onCartConflict?: (foodId: number, foodName: string) => void;
  onAddFailed?: (foodName: string, message?: string) => void;
  onNetworkError?: () => void;
  analyticsEventName?: string;
  getId: (food: T) => number;
  getName: (food: T) => string;
  isAuthenticated?: boolean;
  onAuthRequired?: () => void;
}

export function useCart<T>(options: UseCartOptions<T>) {
  const {
    cartRefreshToken = 0,
    onCartChange,
    onCartConflict,
    onAddFailed,
    onNetworkError,
    analyticsEventName = 'add_to_cart',
    getId,
    getName,
    isAuthenticated = true,
    onAuthRequired,
  } = options;

  const [cartMap, setCartMap] = useState<Record<number, CartItemState>>({});

  const syncCart = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await authenticatedFetch(CART_VIEW_URL);
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;
      const items: Array<{ food_id: number; cart_id: number; quantity: number }> = json.items || [];
      const map: Record<number, CartItemState> = {};
      items.forEach(item => {
        map[item.food_id] = { quantity: item.quantity, cartId: item.cart_id, loading: false };
      });
      setCartMap(map);
    } catch {
      // non-critical — leave existing state
    }
  }, [isAuthenticated]);

  useEffect(() => {
    syncCart();
  }, [syncCart, cartRefreshToken]);

  const setItemLoading = (foodId: number, loading: boolean) => {
    setCartMap(prev => prev[foodId] ? { ...prev, [foodId]: { ...prev[foodId], loading } } : prev);
  };

  const handleIncrement = async (food: T) => {
    if (!isAuthenticated) {
      onAuthRequired?.();
      return;
    }
    const foodId = getId(food);
    const foodName = getName(food);
    const current = cartMap[foodId];
    if (current && current.loading) return;

    if (!current || current.quantity === 0) {
      setCartMap(prev => ({ ...prev, [foodId]: { quantity: 0, cartId: 0, loading: true } }));
      try {
        const res = await authenticatedFetch(CART_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ food_id: foodId, quantity: 1, replace_cart: false }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          trackFood(analyticsEventName, foodName);
          await syncCart();
          onCartChange?.();
        } else if (res.status === 409 || (json.message && json.message.toLowerCase().includes('different kitchen'))) {
          setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
          onCartConflict?.(foodId, foodName);
        } else {
          setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
          onAddFailed?.(foodName, json.message);
        }
      } catch {
        setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
        onNetworkError?.();
      }
    } else {
      const newQty = current.quantity + 1;
      setCartMap(prev => ({ ...prev, [foodId]: { ...current, loading: true } }));
      try {
        const res = await authenticatedFetch(CART_UPDATE_URL(current.cartId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQty }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => ({ ...prev, [foodId]: { ...current, quantity: newQty, loading: false } }));
          onCartChange?.();
        } else {
          setItemLoading(foodId, false);
        }
      } catch {
        setItemLoading(foodId, false);
      }
    }
  };

  const handleDecrement = async (food: T) => {
    const foodId = getId(food);
    const current = cartMap[foodId];
    if (!current || current.quantity === 0 || current.loading) return;

    setCartMap(prev => ({ ...prev, [foodId]: { ...current, loading: true } }));

    if (current.quantity === 1) {
      try {
        const res = await authenticatedFetch(CART_REMOVE_ITEM_URL(current.cartId), { method: 'DELETE' });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
          onCartChange?.();
        } else {
          setItemLoading(foodId, false);
        }
      } catch {
        setItemLoading(foodId, false);
      }
    } else {
      const newQty = current.quantity - 1;
      try {
        const res = await authenticatedFetch(CART_UPDATE_URL(current.cartId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQty }),
        });
        const json = await res.json();
        if (res.ok && json.success) {
          setCartMap(prev => ({ ...prev, [foodId]: { ...current, quantity: newQty, loading: false } }));
          onCartChange?.();
        } else {
          setItemLoading(foodId, false);
        }
      } catch {
        setItemLoading(foodId, false);
      }
    }
  };

  const replaceCartItem = async (foodId: number, foodName: string) => {
    setCartMap(prev => ({ ...prev, [foodId]: { quantity: 0, cartId: 0, loading: true } }));
    try {
      const res = await authenticatedFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ food_id: foodId, quantity: 1, replace_cart: true }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        trackFood(analyticsEventName, foodName);
        await syncCart();
        onCartChange?.();
      } else {
        setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
      }
    } catch {
      setCartMap(prev => { const n = { ...prev }; delete n[foodId]; return n; });
    }
  };

  return { cartMap, syncCart, handleIncrement, handleDecrement, replaceCartItem };
}
